import { useCallback, useContext, useEffect } from "react";

import { AuthContext } from "../../../Auth/AuthContext";
import { useSocketAuthenticated } from "../../SocketContext";
import api from "../../../../services/api";

import { useGlobalRegisters } from "../../../GlobalRegisters/GlobalRegistersContext";

const SocketTicketsList = ({
  dispatch,
  filter,
	selectedQueueIds,
  selectedUserIds,
	selectedConnectionIds,
  status,
	ticketsListSortMethodSetting
}) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { getSocket } = useSocketAuthenticated();
  const { user } = useContext(AuthContext);

	const { getSetting, isLoading } = useGlobalRegisters();



	// 	***************
	// 	** Functions **
	// 	***************
	const shouldUpdateTicket = (ticket) => {
		if (user.isDefault) return true;

		return !filter
			&& (!ticket.userId || ticket.userId === user?.id || ticket.userIdInternalChat === user?.id || ticket.ticketUsers?.some(ticketUser => ticketUser.userId === user.id) || localStorage.getItem("showAllTickets") === "1")
			&& (!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1  || ticket.userId === user?.id)
			&& !(ticket.hasMessageFromPhone && user?.profile === "user" && ticket.userId !== user?.id && ticket.status === "open");
	};

	const notBelongsToUserQueues = (ticket) => {
		return !user.isDefault
			&& ticket.queueId
			&& selectedQueueIds.indexOf(ticket.queueId) === -1
			&& ticket.userId !== user?.id;
	};

	const notBelongsToUserWhatsapps = async (ticket) => {
		if (!ticket) return true;

		let ticketVisible;

		if (user.isDefault || [1, 2, 3, 4].includes(ticket.type)) {
			ticketVisible = true;
		}
		else {
			const userPermissionsByChip = getSetting("userPermissionsByChip");

			ticketVisible = userPermissionsByChip === "disabled" || user.whatsapps === undefined
				? true
				: user.whatsapps.length === 0;

			if (!ticketVisible) {
				user.whatsapps?.forEach((whatsapp) => {
					if (whatsapp.id === ticket.whatsappId) {
						ticketVisible = true;
						return;
					}
				});
			}
		}

		return ticketVisible;
	}



	// 	***************
	// 	** Callbacks **
	// 	***************
	const checkFilteredIds = useCallback((filteredIdsArray, filteredProperty, data, shouldCheckAction) => {
		const shouldValidateParametersCondition = shouldCheckAction
			? data.action === "updateTicket" && data.ticket?.status === "open" && filteredIdsArray
			: data.ticket?.status === "open" && filteredIdsArray;

		if (shouldValidateParametersCondition) {
			const isFilteredPropertyExistentCondition = data.ticket[filteredProperty] !== null && data.ticket[filteredProperty] !== undefined;
			const isFilteredPropertyInexistentAndIsFilteredIdArrayEmptyCondition = (data.ticket[filteredProperty] === null || data.ticket[filteredProperty] === undefined) && filteredIdsArray.length === 0;

			if (isFilteredPropertyExistentCondition) {
				return filteredIdsArray.length === 0 || filteredIdsArray.includes(data.ticket[filteredProperty]);
			}

			else if (isFilteredPropertyInexistentAndIsFilteredIdArrayEmptyCondition) {
				return true;
			}

			else { return false; }
		}
		else { return true; }
	}, []);

	const checkShowAllTicketsSetting = useCallback((data, shouldCheckAction) => {
		if (shouldCheckAction) {
			return localStorage.getItem("showAllTickets") === "0" && data.ticket?.status === "open" && data.action === "updateTicket"
				? data.ticket.userId === user.id || data.ticket.userIdInternalChat === user.id
				: true;
		}
		else {
			return localStorage.getItem("showAllTickets") === "0" && data.ticket?.status === "open"
				? data.ticket.userId === user.id || data.ticket.userIdInternalChat === user.id
				: true;
		}		
	}, [user]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
		if (isLoading) return;

		const socket = getSocket();

		if (socket) {
			const handleTicket = async (data) => {
				if (data.status && data.status !== status) return;

				const isTenantIdValid = `${api.defaults.headers.tenantId}` === `${data.tenantId}`;
				const userIdsFilterCondition = checkFilteredIds(selectedUserIds, "userId", data, true);
				const connectionIdsFilterCondition = checkFilteredIds(selectedConnectionIds, "whatsappId", data, true);
				const showAllTicketsCondition = checkShowAllTicketsSetting(data, true);
				const noTicketPermissionByUser = await notBelongsToUserWhatsapps(data.ticket);

				const shouldTriggerUpdateUnreadTicket = data.action === "updateUnreadTicket" && isTenantIdValid;
				const shouldTriggerUpdateTicket = data.action === "updateTicket" && userIdsFilterCondition && connectionIdsFilterCondition && showAllTicketsCondition && shouldUpdateTicket(data.ticket) && noTicketPermissionByUser && isTenantIdValid;
				const shouldTriggerUpdateDeleteTicket = data.action === "updateTicket" && data.ticket && notBelongsToUserQueues(data.ticket) && isTenantIdValid;
				const shouldTriggerDeleteTicket = data.action === "deleteTicket" && isTenantIdValid;
				const shouldTriggerDeleteTicketForSingleUser = data.action === "deleteTicketForSingleUser" && user.id === data.userId && isTenantIdValid;
	
				if (shouldTriggerUpdateUnreadTicket) {
					dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
				}
				
				if (shouldTriggerUpdateTicket) {
					dispatch({ type: "UPDATE_TICKET", payload: data.ticket, misc: { sortingMethod: ticketsListSortMethodSetting } });
				}

				if (shouldTriggerUpdateDeleteTicket) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				}
	
				if (shouldTriggerDeleteTicket) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
				}

				if (shouldTriggerDeleteTicketForSingleUser) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
				}
			};
	
			const handleAppMessage = async (data) => {
				if (data.status && data.status !== status) return;

				const isTenantIdValid = `${api.defaults.headers.tenantId}` === `${data.tenantId}`;
				const userIdsFilterCondition = checkFilteredIds(selectedUserIds, "userId", data, false);
				const connectionIdsFilterCondition = checkFilteredIds(selectedConnectionIds, "whatsappId", data, false);
				const showAllTicketsCondition = checkShowAllTicketsSetting(data, false);
				const noTicketPermissionByUser = await notBelongsToUserWhatsapps(data.ticket);

				const shouldTriggerUpdateTicket = data.action === "createAppMessage" && userIdsFilterCondition && connectionIdsFilterCondition && showAllTicketsCondition && shouldUpdateTicket(data.ticket) && noTicketPermissionByUser && isTenantIdValid;

				if (shouldTriggerUpdateTicket) {
					dispatch({ type: "UPDATE_TICKET", payload: data.ticket, misc: { sortingMethod: ticketsListSortMethodSetting } });
				}
			};
	
			socket.on("ticket", handleTicket);
			socket.on("appMessage", handleAppMessage);
	
			return () => {
				socket.off("ticket", handleTicket);
				socket.off("appMessage", handleAppMessage);
			};
		}
		

    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSetting, isLoading, dispatch, filter, selectedQueueIds, selectedUserIds, selectedConnectionIds, status]);



  //  ************
  //  ** Return **
  //  ************
  return (<></>);
};

export default SocketTicketsList;